import {
  type GenqlResult,
  type Genql,
} from '@bizzdesign/bizzdesign-sdk-type-genql';
import { isNotUndefinedOrNull } from '@bizzdesign/sdk-internal';
import {
  type AppClient,
  type AttributeMappings,
  type CreateAppClientResult,
  type Group,
  type GroupMember,
  type GroupMemberResult,
  type IdentityProvider,
  type ProviderDetailsOutput,
  type SetTemporaryPassword,
  type Roles,
  type User,
} from '../interfaces';

// The list queries no longer select the per-membership/-member `createdAt` (Neo4j's MEMBER_OF
// relationship does not store it), so map against shapes that omit it.
type MappableMembership = Omit<Genql.GroupMembershipOutput, 'createdAt'>;
type MappableMember = Omit<Genql.GroupMemberOutput, 'createdAt'>;
type MappableUser = Omit<Genql.UserOutput, 'memberships'> & {
  memberships?: (MappableMembership | null)[] | null;
};
type MappableGroup = Omit<Genql.GroupOutput, 'members'> & {
  members?: (MappableMember | null)[] | null;
};

// ==================== Roles (shared) ====================

export function mapRoles(
  rolesOutput: GenqlResult<Genql.RolesOutput> | null | undefined,
): Roles | undefined {
  if (!rolesOutput) return undefined;
  return {
    isAdmin: rolesOutput.isAdmin ?? false,
    isSysAdmin: rolesOutput.isSysAdmin ?? false,
    isPro: rolesOutput.isPro ?? false,
    isCore: rolesOutput.isCore ?? false,
    isBase: rolesOutput.isBase ?? false,
  };
}

// ==================== User ====================

export function mapUser(userOutput: GenqlResult<MappableUser>): User {
  return {
    id: userOutput.id,
    email: userOutput.email,
    givenName: userOutput.givenName ?? undefined,
    familyName: userOutput.familyName ?? undefined,
    origin: userOutput.origin ?? undefined,
    lastActiveAt: userOutput.lastActiveAt ?? undefined,
    firstLoginAt: userOutput.firstLoginAt ?? undefined,
    roles: mapRoles(userOutput.roles),
    memberships: (userOutput.memberships ?? []).map((m) => ({
      groupId: m.groupId,
      groupName: m.groupName,
    })),
    createdAt: userOutput.createdAt,
    updatedAt: userOutput.updatedAt,
    trial: userOutput.trial ?? false,
  };
}

export function mapSetTemporaryPassword(
  setTemporaryPasswordOutput: GenqlResult<Genql.SetTemporaryPasswordOutput>,
): SetTemporaryPassword {
  return {
    temporaryPassword: setTemporaryPasswordOutput.temporaryPassword,
  };
}

// ==================== Group & GroupMember ====================

export function mapGroupMember(
  groupMemberOutput: GenqlResult<MappableMember>,
): GroupMember {
  return {
    userId: groupMemberOutput.userId,
    userEmail: groupMemberOutput.userEmail,
    userFullName: groupMemberOutput.userFullName ?? undefined,
  };
}

export function mapGroup(groupOutput: GenqlResult<MappableGroup>): Group {
  return {
    id: groupOutput.id,
    name: groupOutput.name,
    description: groupOutput.description ?? undefined,
    roles: mapRoles(groupOutput.roles),
    members: (groupOutput.members ?? []).map(mapGroupMember),
    createdAt: groupOutput.createdAt,
    updatedAt: groupOutput.updatedAt,
  };
}

export function mapGroupWithoutMembers(
  groupOutput: GenqlResult<Omit<MappableGroup, 'members'>>,
): Group {
  return mapGroup({ ...groupOutput, members: [] });
}

// ==================== GroupMemberResult (passthrough) ====================

export function mapGroupMemberResult(
  groupMemberIdOutput: GenqlResult<Genql.GroupMemberIdOutput>,
): GroupMemberResult {
  return {
    groupId: groupMemberIdOutput.groupId,
    userId: groupMemberIdOutput.userId,
  };
}

// ==================== Identity Provider ====================

export function mapAttributeMappings(
  attributeMappingsOutput:
    | GenqlResult<Genql.AttributeMappingsOutput>
    | null
    | undefined,
): AttributeMappings | undefined {
  if (
    !attributeMappingsOutput ||
    !isNotUndefinedOrNull(attributeMappingsOutput.emailAttribute) ||
    !isNotUndefinedOrNull(attributeMappingsOutput.familyNameAttribute) ||
    !isNotUndefinedOrNull(attributeMappingsOutput.givenNameAttribute)
  ) {
    return undefined;
  }
  return {
    emailAttribute: attributeMappingsOutput.emailAttribute,
    familyNameAttribute: attributeMappingsOutput.familyNameAttribute,
    givenNameAttribute: attributeMappingsOutput.givenNameAttribute,
  };
}

export function mapProviderDetailsOutput(
  providerDetailsOutput:
    | GenqlResult<Genql.ProviderDetailsOutput>
    | null
    | undefined,
): ProviderDetailsOutput | undefined {
  if (
    !providerDetailsOutput ||
    !isNotUndefinedOrNull(providerDetailsOutput.providerType)
  ) {
    return undefined;
  }
  const oidcDetails = providerDetailsOutput.oidcDetailsOutput;
  const samlDetails = providerDetailsOutput.samlDetailsOutput;
  const providerType = providerDetailsOutput.providerType;
  return {
    providerType,
    oidcDetailsOutput:
      oidcDetails && isNotUndefinedOrNull(oidcDetails.oidcIssuer)
        ? { oidcIssuer: oidcDetails.oidcIssuer, clientId: oidcDetails.clientId }
        : undefined,
    samlDetailsOutput:
      samlDetails && isNotUndefinedOrNull(samlDetails.metaDataUrl)
        ? { metaDataUrl: samlDetails.metaDataUrl }
        : undefined,
  };
}

export function mapIdentityProviderSimple(
  identityProviderInfoOutput: GenqlResult<Genql.IdentityProviderInfoOutput>,
): IdentityProvider {
  return {
    providerName: identityProviderInfoOutput.providerName,
    providerType: identityProviderInfoOutput.providerType ?? undefined,
    state: identityProviderInfoOutput.state ?? undefined,
    createdAt: identityProviderInfoOutput.createdAt,
    updatedAt: identityProviderInfoOutput.updatedAt,
  };
}

export function mapIdentityProvider(
  identityProviderOutput: GenqlResult<Genql.IdentityProviderOutput>,
): IdentityProvider {
  const providerDetailsOutput = mapProviderDetailsOutput(
    identityProviderOutput.providerDetailsOutput,
  );
  return {
    providerName: identityProviderOutput.providerName,
    providerType: providerDetailsOutput?.providerType ?? undefined,
    state: identityProviderOutput.state ?? undefined,
    attributeMappings: mapAttributeMappings(
      identityProviderOutput.attributeMappings,
    ),
    providerDetailsOutput,
    createdAt: identityProviderOutput.createdAt,
    updatedAt: identityProviderOutput.updatedAt,
  };
}

// ==================== App Client ====================

export function mapAppClient(
  appClientOutput: GenqlResult<Genql.AppClientOutput>,
): AppClient {
  return {
    appClientId: appClientOutput.appClientId,
    appClientName: appClientOutput.appClientName,
    authFlow: appClientOutput.authFlow,
    roles: mapRoles(appClientOutput.roles),
    providerNames: appClientOutput.providerNames ?? [],
    accessTokenValidity: appClientOutput.accessTokenValidity ?? undefined,
    createdAt: appClientOutput.createdAt,
    updatedAt: appClientOutput.updatedAt,
    callbackUrls: appClientOutput.callbackUrls ?? [],
    logoutUrls: appClientOutput.logoutUrls ?? [],
  };
}

export function mapCreateAppClientResult(
  createAppClientOutput: GenqlResult<Genql.CreateAppClientOutput>,
  mappedAppClient: AppClient,
): CreateAppClientResult {
  return {
    appClient: mappedAppClient,
    appClientSecret: createAppClientOutput.appClientSecret ?? null,
  };
}

export function mapAppClientFromUpdate(
  appClientOutput: GenqlResult<Genql.AppClientOutput>,
): AppClient {
  return {
    appClientId: appClientOutput.appClientId,
    appClientName: appClientOutput.appClientName,
    authFlow: appClientOutput.authFlow,
    roles: mapRoles(appClientOutput.roles),
    providerNames: appClientOutput.providerNames ?? [],
    accessTokenValidity: appClientOutput.accessTokenValidity ?? undefined,
    createdAt: appClientOutput.createdAt,
    updatedAt: appClientOutput.updatedAt,
    callbackUrls: appClientOutput.callbackUrls ?? [],
    logoutUrls: appClientOutput.logoutUrls ?? [],
  };
}
